import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import referralAdminApi from '../../services/referralAdminApi';
import companyApi from '../../services/companyApi';
import { PageHeader, SegmentedToggle } from '../../components/ui';
import ReferralRewardsTable from './components/ReferralRewardsTable';
import ReferralSettingsForm from './components/ReferralSettingsForm';
import RewardActionModal from './components/RewardActionModal';
import RewardDetailModal from './components/RewardDetailModal';
import ReferralAdminSkeleton from './components/ReferralAdminSkeleton';
import './referral-admin.css';

const REWARDS_PAGE_SIZE = 20;

const SHELL_CLASS =
  'referral-admin-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8';

const ReferralAdmin = () => {
  const [activeTab, setActiveTab] = useState('rewards');

  const [rewards, setRewards] = useState([]);
  const [rewardsPagination, setRewardsPagination] = useState({
    pageNumber: 1,
    pageSize: REWARDS_PAGE_SIZE,
    totalRecords: 0,
  });
  const [filter, setFilter] = useState({
    status: '',
    fraudonly: false,
    companyid: '',
  });
  const [companyOptions, setCompanyOptions] = useState([]);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState({ rewards: true, settings: true });
  const [actionModal, setActionModal] = useState({
    open: false,
    action: 'approve',
    reward: null,
  });
  const [detailModal, setDetailModal] = useState({ open: false, reward: null });

  const fetchRewards = useCallback(async (pageNumber, currentFilter) => {
    setLoading((prev) => ({ ...prev, rewards: true }));
    try {
      const statusPart =
        currentFilter.status !== '' ? `&status=${currentFilter.status}` : '';
      const fraudPart = currentFilter.fraudonly ? '&fraudonly=true' : '';
      const companyPart = currentFilter.companyid
        ? `&companyid=${currentFilter.companyid}`
        : '';
      const query = `?pageNumber=${pageNumber}&pageSize=${REWARDS_PAGE_SIZE}${statusPart}${fraudPart}${companyPart}`;
      const res = await referralAdminApi.rewards(query);
      setRewards(res.data?.data || []);
      setRewardsPagination({
        pageNumber,
        pageSize: REWARDS_PAGE_SIZE,
        totalRecords: res.data?.totalRecords || 0,
      });
    } catch {
      setRewards([]);
      message.error('Failed to load referral rewards');
    } finally {
      setLoading((prev) => ({ ...prev, rewards: false }));
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    setLoading((prev) => ({ ...prev, settings: true }));
    try {
      const res = await referralAdminApi.getSettings();
      setSettings(res.data?.data || null);
    } catch {
      message.error('Failed to load referral settings');
    } finally {
      setLoading((prev) => ({ ...prev, settings: false }));
    }
  }, []);

  const fetchCompanyOptions = useCallback(async () => {
    try {
      const res = await companyApi.lookup();
      const list = res.data?.data || [];
      setCompanyOptions(list.map((c) => ({ value: c.id, label: c.name })));
    } catch {
      setCompanyOptions([]);
    }
  }, []);

  // Mount'ta rewards (varsayılan sekme) + settings + şirket lookup paralel.
  useEffect(() => {
    fetchRewards(1, { status: '', fraudonly: false, companyid: '' });
    fetchSettings();
    fetchCompanyOptions();
  }, [fetchRewards, fetchSettings, fetchCompanyOptions]);

  const handleFilterChange = (next) => {
    setFilter(next);
    fetchRewards(1, next);
  };

  const handlePageChange = (page) => {
    fetchRewards(page, filter);
  };

  const handleAction = (action, reward) =>
    setActionModal({ open: true, action, reward });

  const closeActionModal = () =>
    setActionModal((prev) => ({ ...prev, open: false }));

  const handleRowClick = (reward) => setDetailModal({ open: true, reward });

  const closeDetailModal = () =>
    setDetailModal((prev) => ({ ...prev, open: false }));

  // Aksiyon/re-evaluate başarılı -> mevcut sayfa + filtre ile listeyi tazele.
  const handleChanged = () => {
    fetchRewards(rewardsPagination.pageNumber, filter);
  };

  // İlk paint: ilk rewards yüklemesi tamamlanana kadar skeleton.
  if (loading.rewards && rewards.length === 0) {
    return <ReferralAdminSkeleton />;
  }

  return (
    <div className={SHELL_CLASS}>
      <div className="mx-auto max-w-6xl space-y-6">
        <PageHeader
          title="Referral Program"
          description="Moderate referral rewards and configure program settings."
        />

        <SegmentedToggle
          ariaLabel="Referral admin sections"
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { value: 'rewards', label: 'Rewards' },
            { value: 'settings', label: 'Settings' },
          ]}
        />

        {activeTab === 'rewards' ? (
          <ReferralRewardsTable
            data={rewards}
            loading={loading.rewards}
            pagination={rewardsPagination}
            onPageChange={handlePageChange}
            filter={filter}
            onFilterChange={handleFilterChange}
            onAction={handleAction}
            onRowClick={handleRowClick}
            companyOptions={companyOptions}
          />
        ) : (
          <ReferralSettingsForm
            settings={settings}
            loading={loading.settings}
            onSaved={fetchSettings}
          />
        )}

        <RewardActionModal
          open={actionModal.open}
          action={actionModal.action}
          reward={actionModal.reward}
          onClose={closeActionModal}
          onDone={handleChanged}
        />

        <RewardDetailModal
          open={detailModal.open}
          reward={detailModal.reward}
          onClose={closeDetailModal}
          onChanged={handleChanged}
        />
      </div>
    </div>
  );
};

export default ReferralAdmin;
